import React, { useState } from 'react';
import { CheckGroup } from './CheckGroup';
import { StatusBadge, StatusType } from './StatusBadge';
import { FileText, Download, Share2, Calendar, UserCheck, AlertCircle, Settings, ChevronDown, Mail, Link2, Copy, FileDown, Printer, Building2, Users } from 'lucide-react';
import { CheckStatus } from './ComplianceCheck';
import { motion, AnimatePresence } from 'framer-motion';
const complianceData = [{
  category: 'Truth & Accuracy',
  checks: [{
    title: 'Truth in Advertising',
    description: 'Marketing content does not include false, exaggerated, or deceptive claims.',
    status: 'passed' as CheckStatus
  }, {
    title: 'Clear & Conspicuous Disclosures',
    description: 'All required disclosures (APR, loan terms, NMLS ID, equal housing statements) are properly displayed and readable.',
    status: 'pending' as CheckStatus,
    pendingData: {
      type: 'nmls' as const,
      detectedValue: '123456'
    }
  }, {
    title: 'APR Accuracy Check',
    description: 'All rates, APRs, and cost examples are calculated correctly and match current pricing guidelines.',
    status: 'pending' as CheckStatus,
    pendingData: {
      type: 'rateSheet' as const
    }
  }, {
    title: 'Trigger Term Disclosure Compliance',
    description: "Any trigger terms (e.g., 'monthly payment', 'fixed rate', '0% down') are accompanied by the required additional disclosures (Regulation Z).",
    status: 'warning' as CheckStatus,
    remediation: "Add full disclosure after 'low monthly payment' trigger term. Include APR, term length, and example payment calculation.",
    location: {
      section: 'Hero Section',
      excerpt: 'Lock in your low monthly payment before rates go back up.'
    }
  }, {
    title: 'UDAAP Compliance Check',
    description: 'Content does not contain language that could be considered unfair, deceptive, or abusive.',
    status: 'passed' as CheckStatus
  }]
}, {
  category: 'Fair Lending & Equal Housing',
  checks: [{
    title: 'Fair Lending Language Review',
    description: 'No content includes discouraging language, targeting biases, or improper exclusion of any protected class (ECOA / FHA).',
    status: 'passed' as CheckStatus
  }, {
    title: 'Equal Housing Lender Requirements',
    description: 'Equal Housing logo and required statements are present where required.',
    status: 'passed' as CheckStatus
  }, {
    title: 'Non-Discrimination in Imagery',
    description: 'Images, personas, and sample scenarios reflect inclusive and fair-lending-appropriate representation.',
    status: 'passed' as CheckStatus
  }, {
    title: 'Target Audience Eligibility',
    description: 'Marketing is not directed at audiences who are ineligible for the promoted product or program.',
    status: 'passed' as CheckStatus
  }]
}, {
  category: 'Licensing & Identification',
  checks: [{
    title: 'Licensing & Identifier Accuracy',
    description: 'NMLS IDs, state license numbers, and corporate legal names are included and accurate.',
    status: 'passed' as CheckStatus
  }, {
    title: 'State-Specific Advertising Requirements',
    description: 'State-level disclosure rules (e.g., TX, CA, MA) are met for the intended distribution region.',
    status: 'pending' as CheckStatus,
    pendingData: {
      type: 'stateLicenses' as const
    }
  }, {
    title: 'No Implicit Government Endorsement',
    description: 'Content does not imply affiliation with HUD, FHA, VA, USDA, or other government agencies.',
    status: 'passed' as CheckStatus
  }]
}, {
  category: 'Marketing Claims',
  checks: [{
    title: "No Improper 'Pre-Approval' Terms",
    description: 'Terms like pre-approval, pre-qualification, guaranteed approval are correctly used and not misleading.',
    status: 'passed' as CheckStatus
  }, {
    title: 'Bait-and-Switch Guardrail',
    description: "Offered terms are available, realistic, and consistent with lender's current rate sheets.",
    status: 'passed' as CheckStatus
  }, {
    title: 'Rate Lock & Cost Savings Claims',
    description: 'Any statements about savings, rate reductions, or lock guarantees are substantiated.',
    status: 'passed' as CheckStatus
  }, {
    title: 'Home Value or Payment Estimates',
    description: 'Automated estimates meet model accuracy thresholds and include appropriate disclaimers.',
    status: 'passed' as CheckStatus
  }, {
    title: 'No Prohibited Marketing Tactics',
    description: 'Content does not use scare tactics, unverifiable urgency messaging, or high-pressure phrasing.',
    status: 'passed' as CheckStatus
  }]
}, {
  category: 'Program Compliance',
  checks: [{
    title: 'Program-Specific Requirements',
    description: 'All program claims meet investor, agency, and lender rule requirements (FHA, VA, USDA, Jumbo, Non-QM).',
    status: 'passed' as CheckStatus
  }, {
    title: 'Internal Lender Compliance Rules',
    description: 'All lender-specific brand, disclaimer, and language rules are met.',
    status: 'passed' as CheckStatus
  }]
}, {
  category: 'Data & Communications',
  checks: [{
    title: 'Contact Method Compliance',
    description: 'SMS requires opt-in; email includes unsubscribe link; phone outreach disclaimers are present when applicable (TCPA / CAN-SPAM).',
    status: 'passed' as CheckStatus
  }, {
    title: 'Data Privacy & Consent Review',
    description: 'No improper claims about data usage; privacy statements align with compliance policy (CCPA / GLBA).',
    status: 'passed' as CheckStatus
  }, {
    title: 'Social Proof & Testimonials',
    description: 'Any quoted reviews or borrower stories comply with advertising rules and are not misleading or unverifiable.',
    status: 'passed' as CheckStatus
  }]
}, {
  category: 'Partner Marketing',
  checks: [{
    title: 'Realtor / Partner Co-Marketing',
    description: 'Content avoids RESPA Section 8 violations; no indication of quid-pro-quo or improper referrals.',
    status: 'passed' as CheckStatus
  }]
}, {
  category: 'Technology & Operations',
  checks: [{
    title: 'AI Safety & Hallucination Check',
    description: 'All autogenerated statements are grounded in verifiable lender data; no fabricated numbers, laws, or programs.',
    status: 'passed' as CheckStatus
  }, {
    title: 'Document Version Control Check',
    description: "Lender's latest brand guidelines, legal copy, and compliance templates are being used.",
    status: 'passed' as CheckStatus
  }, {
    title: 'Accessibility Review',
    description: 'Digital content meets readability, alt-text, and contrast accessibility requirements (ADA / WCAG).',
    status: 'passed' as CheckStatus
  }, {
    title: 'Recordkeeping Compliance',
    description: 'Marketing asset can be exported for audit retention as required by federal and state regulators.',
    status: 'passed' as CheckStatus
  }]
}];
export function ComplianceReview() {
  const [shareDropdownOpen, setShareDropdownOpen] = useState(false);
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const totalChecks = complianceData.reduce((acc, group) => acc + group.checks.length, 0);
  const passedChecks = complianceData.reduce((acc, group) => acc + group.checks.filter(c => c.status === 'passed').length, 0);
  const failedChecks = complianceData.reduce((acc, group) => acc + group.checks.filter(c => c.status === 'failed').length, 0);
  const warningChecks = complianceData.reduce((acc, group) => acc + group.checks.filter(c => c.status === 'warning').length, 0);
  const pendingChecks = complianceData.reduce((acc, group) => acc + group.checks.filter(c => c.status === 'pending').length, 0);
  const overallStatus: StatusType = failedChecks > 0 ? 'failed' : warningChecks > 0 ? 'warning' : pendingChecks > 0 ? 'pending' : 'passed';
  const completedChecks = totalChecks - pendingChecks;
  const passRate = totalChecks > 0 ? Math.round(passedChecks / totalChecks * 100) : 0;
  const profileCompletion = totalChecks > 0 ? Math.round((totalChecks - pendingChecks) / totalChecks * 100) : 0;
  const handleDataSubmit = (groupIndex: number, checkIndex: number, value: string) => {
    console.log(`Data submitted for group ${groupIndex}, check ${checkIndex}:`, value);
  };
  return <div className="min-h-screen bg-slate-50 font-sans text-slate-900" data-id="element-48">
      {/* Top Navigation Bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10" data-id="element-49">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-50">
          <div className="flex items-center justify-between h-16" data-id="element-51">
            <div className="flex items-center gap-3" data-id="element-52">
              <span className="font-bold text-lg tracking-tight text-slate-900" data-id="element-53">
                Luthor
              </span>
              <span className="hidden sm:inline-block h-4 w-px bg-slate-300 mx-2" data-id="element-54"></span>
              <span className="hidden sm:inline-block text-sm font-medium text-slate-500" data-id="element-55">
                Compliance Review
              </span>
            </div>
            <div className="flex items-center gap-3" data-id="element-56">
              {/* Profile Dropdown */}
              {pendingChecks > 0 && <div className="relative" data-id="element-57">
                  <button onClick={() => {
                setProfileDropdownOpen(!profileDropdownOpen);
                setShareDropdownOpen(false);
                setExportDropdownOpen(false);
              }} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded transition-colors border border-slate-200" data-id="element-58">
                    <Settings className="w-4 h-4" data-id="element-59" />
                    <span className="hidden sm:inline" data-id="element-60">Settings</span>
                    <ChevronDown className="w-3.5 h-3.5" data-id="element-61" />
                  </button>

                  <AnimatePresence data-id="element-62">
                    {profileDropdownOpen && <motion.div initial={{
                  opacity: 0,
                  y: -8
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -8
                }} transition={{
                  duration: 0.15
                }} className="absolute right-0 mt-2 w-64 bg-white rounded shadow-lg border border-slate-200 py-1 z-20" data-id="element-63">
                        <div className="px-4 py-3 border-b border-slate-100" data-id="element-64">
                          <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1" data-id="element-65">
                            Company Profile
                          </div>
                          <div className="flex items-center justify-between" data-id="element-66">
                            <span className="text-sm font-semibold text-slate-900" data-id="element-67">
                              {profileCompletion}% Complete
                            </span>
                            <span className="text-xs text-slate-500" data-id="element-68">
                              {pendingChecks} pending
                            </span>
                          </div>
                          <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-69">
                            <div className="h-full bg-slate-400 rounded-full transition-all duration-300" style={{
                        width: `${profileCompletion}%`
                      }} data-id="element-70" />
                          </div>
                        </div>

                        <button className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-71">
                          <Building2 className="w-4 h-4 text-slate-400" data-id="element-72" />
                          <div className="text-left" data-id="element-73">
                            <div className="font-medium" data-id="element-74">
                              Company Information
                            </div>
                            <div className="text-xs text-slate-500" data-id="element-75">
                              NMLS ID, licenses
                            </div>
                          </div>
                        </button>
                        <button className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-76">
                          <FileText className="w-4 h-4 text-slate-400" data-id="element-77" />
                          <div className="text-left" data-id="element-78">
                            <div className="font-medium" data-id="element-79">Rate Sheets</div>
                            <div className="text-xs text-slate-500" data-id="element-80">
                              Current pricing data
                            </div>
                          </div>
                        </button>
                        <button className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-81">
                          <Users className="w-4 h-4 text-slate-400" data-id="element-82" />
                          <div className="text-left" data-id="element-83">
                            <div className="font-medium" data-id="element-84">
                              Team & Permissions
                            </div>
                            <div className="text-xs text-slate-500" data-id="element-85">
                              Manage access
                            </div>
                          </div>
                        </button>

                        <div className="border-t border-slate-100 mt-1 pt-1" data-id="element-86">
                          <button className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-87">
                            <Settings className="w-4 h-4 text-slate-400" data-id="element-88" />
                            <div className="text-left" data-id="element-89">
                              <div className="font-medium" data-id="element-90">All Settings</div>
                            </div>
                          </button>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>}

              {/* Share Dropdown */}
              <div className="relative" data-id="element-91">
                <button onClick={() => {
                setShareDropdownOpen(!shareDropdownOpen);
                setExportDropdownOpen(false);
                setProfileDropdownOpen(false);
              }} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded transition-colors" data-id="element-92">
                  <Share2 className="w-4 h-4" data-id="element-93" />
                  <span className="hidden sm:inline" data-id="element-94">Share</span>
                  <ChevronDown className="w-3.5 h-3.5" data-id="element-95" />
                </button>

                <AnimatePresence data-id="element-96">
                  {shareDropdownOpen && <motion.div initial={{
                  opacity: 0,
                  y: -8
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -8
                }} transition={{
                  duration: 0.15
                }} className="absolute right-0 mt-2 w-56 bg-white rounded shadow-lg border border-slate-200 py-1 z-20" data-id="element-97">
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-98">
                        <Mail className="w-4 h-4 text-slate-400" data-id="element-99" />
                        <div className="text-left" data-id="element-100">
                          <div className="font-medium" data-id="element-101">Email Report</div>
                          <div className="text-xs text-slate-500" data-id="element-102">
                            Send via email
                          </div>
                        </div>
                      </button>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-103">
                        <Link2 className="w-4 h-4 text-slate-400" data-id="element-104" />
                        <div className="text-left" data-id="element-105">
                          <div className="font-medium" data-id="element-106">Copy Link</div>
                          <div className="text-xs text-slate-500" data-id="element-107">
                            Share via URL
                          </div>
                        </div>
                      </button>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-108">
                        <Copy className="w-4 h-4 text-slate-400" data-id="element-109" />
                        <div className="text-left" data-id="element-110">
                          <div className="font-medium" data-id="element-111">Copy to Clipboard</div>
                          <div className="text-xs text-slate-500" data-id="element-112">
                            Copy report summary
                          </div>
                        </div>
                      </button>
                    </motion.div>}
                </AnimatePresence>
              </div>

              {/* Export Dropdown */}
              <div className="relative" data-id="element-113">
                <button onClick={() => {
                setExportDropdownOpen(!exportDropdownOpen);
                setShareDropdownOpen(false);
                setProfileDropdownOpen(false);
              }} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded shadow-sm transition-colors" data-id="element-114">
                  <Download className="w-4 h-4" data-id="element-115" />
                  <span data-id="element-116">Export</span>
                  <ChevronDown className="w-3.5 h-3.5" data-id="element-117" />
                </button>

                <AnimatePresence data-id="element-118">
                  {exportDropdownOpen && <motion.div initial={{
                  opacity: 0,
                  y: -8
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -8
                }} transition={{
                  duration: 0.15
                }} className="absolute right-0 mt-2 w-56 bg-white rounded shadow-lg border border-slate-200 py-1 z-20" data-id="element-119">
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-120">
                        <FileDown className="w-4 h-4 text-slate-400" data-id="element-121" />
                        <div className="text-left" data-id="element-122">
                          <div className="font-medium" data-id="element-123">Export as PDF</div>
                          <div className="text-xs text-slate-500" data-id="element-124">
                            Full report with details
                          </div>
                        </div>
                      </button>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-125">
                        <FileDown className="w-4 h-4 text-slate-400" data-id="element-126" />
                        <div className="text-left" data-id="element-127">
                          <div className="font-medium" data-id="element-128">Export as CSV</div>
                          <div className="text-xs text-slate-500" data-id="element-129">
                            Data for spreadsheets
                          </div>
                        </div>
                      </button>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-130">
                        <Printer className="w-4 h-4 text-slate-400" data-id="element-131" />
                        <div className="text-left" data-id="element-132">
                          <div className="font-medium" data-id="element-133">Print Report</div>
                          <div className="text-xs text-slate-500" data-id="element-134">
                            Print-friendly version
                          </div>
                        </div>
                      </button>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-135">
        {/* Header Section */}
        <div className="bg-white rounded border border-slate-200 p-6 mb-8" data-id="element-136">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6" data-id="element-137">
            <div data-id="element-138">
              <div className="flex items-center gap-3 mb-2" data-id="element-139">
                <StatusBadge status={overallStatus} size="lg" data-id="element-140" />
                <span className="text-sm text-slate-500 font-medium" data-id="element-141">
                  ID: #MK-2024-8392
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2" data-id="element-142">
                Q4 Refinance Email Campaign - "Rate Drop"
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500" data-id="element-143">
                <div className="flex items-center gap-1.5" data-id="element-144">
                  <FileText className="w-4 h-4" data-id="element-145" />
                  <span data-id="element-146">Email Template</span>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-147">
                  <Calendar className="w-4 h-4" data-id="element-148" />
                  <span data-id="element-149">Reviewed Oct 24, 2024</span>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-150">
                  <UserCheck className="w-4 h-4" data-id="element-151" />
                  <span data-id="element-152">Auto-Review Engine v2.4</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4 bg-slate-50 p-4 rounded border border-slate-100" data-id="element-153">
              <div className="text-center px-2" data-id="element-154">
                <div className="text-3xl font-bold text-slate-900" data-id="element-155">
                  {completedChecks}/{totalChecks}
                </div>
                <div className="text-xs font-medium text-slate-500 uppercase tracking-wide" data-id="element-156">
                  Complete
                </div>
              </div>
              <div className="w-px h-10 bg-slate-200" data-id="element-157"></div>
              <div className="text-center px-2" data-id="element-158">
                <div className={`text-3xl font-bold ${overallStatus === 'failed' ? 'text-red-600' : overallStatus === 'warning' ? 'text-amber-600' : overallStatus === 'pending' ? 'text-slate-600' : 'text-emerald-600'}`} data-id="element-159">
                  {passRate}%
                </div>
                <div className={`text-xs font-medium uppercase tracking-wide ${overallStatus === 'failed' ? 'text-red-600' : overallStatus === 'warning' ? 'text-amber-600' : overallStatus === 'pending' ? 'text-slate-600' : 'text-emerald-600'}`} data-id="element-160">
                  Pass Rate
                </div>
              </div>
            </div>
          </div>

          {/* Alert Banner */}
          {overallStatus !== 'passed' && <div className={`mt-6 p-4 rounded border flex items-start gap-3 ${overallStatus === 'failed' ? 'bg-red-50 border-red-200' : overallStatus === 'warning' ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`} data-id="element-161">
              <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${overallStatus === 'failed' ? 'text-red-600' : overallStatus === 'warning' ? 'text-amber-600' : 'text-slate-600'}`} data-id="element-162" />
              <div data-id="element-163">
                <h3 className={`text-sm font-semibold mb-1 ${overallStatus === 'failed' ? 'text-red-900' : overallStatus === 'warning' ? 'text-amber-900' : 'text-slate-900'}`} data-id="element-164">
                  {overallStatus === 'failed' ? `${failedChecks} Critical Issue${failedChecks > 1 ? 's' : ''} Detected` : overallStatus === 'warning' ? `${warningChecks} Warning${warningChecks > 1 ? 's' : ''} Detected` : `${pendingChecks} Check${pendingChecks > 1 ? 's' : ''} Need Setup`}
                </h3>
                <p className={`text-sm ${overallStatus === 'failed' ? 'text-red-700' : overallStatus === 'warning' ? 'text-amber-700' : 'text-slate-700'}`} data-id="element-165">
                  {overallStatus === 'failed' ? 'This marketing asset cannot be approved until all failed checks are resolved.' : overallStatus === 'warning' ? 'Review warnings to improve compliance confidence.' : 'Complete your company profile to verify all compliance checks. Click "Setup" on pending checks to provide required data.'}
                </p>
              </div>
            </div>}
        </div>

        {/* Summary */}
        <div className="flex items-center justify-between mb-6" data-id="element-166">
          <h2 className="text-lg font-semibold text-slate-900" data-id="element-167">
            Compliance Breakdown
          </h2>
          <div className="text-sm text-slate-500" data-id="element-168">
            {pendingChecks > 0 && <span className="text-slate-600 font-medium mr-3" data-id="element-169">
                {pendingChecks} pending
              </span>}
            {failedChecks > 0 && <span className="text-red-600 font-medium mr-3" data-id="element-170">
                {failedChecks} failed
              </span>}
            {warningChecks > 0 && <span className="text-amber-600 font-medium mr-3" data-id="element-171">
                {warningChecks} warning{warningChecks > 1 ? 's' : ''}
              </span>}
            <span className="text-slate-900 font-medium" data-id="element-172">
              {passedChecks} passed
            </span>
          </div>
        </div>

        {/* Check Groups */}
        <div className="space-y-4" data-id="element-173">
          {complianceData.map((group, groupIndex) => <CheckGroup key={groupIndex} title={group.category} checks={group.checks} defaultExpanded={groupIndex === 0 || groupIndex === 2} onDataSubmit={(checkIndex, value) => handleDataSubmit(groupIndex, checkIndex, value)} data-id="element-174" />)}
        </div>

        {/* Footer */}
        <div className="mt-12 text-center border-t border-slate-200 pt-8 pb-12" data-id="element-175">
          <p className="text-sm text-slate-500 mb-2" data-id="element-176">
            This report was automatically generated by Luthor.
          </p>
          <p className="text-xs text-slate-400" data-id="element-177">
            Audit Trail ID: 8f7e-29a1-4b3c-9d8e • Compliance Standards: 2024.Q4
          </p>
        </div>
      </main>
    </div>;
}